import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormField, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatListOption, MatSelectionList } from '@angular/material/list';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { merge } from 'lodash-es';
import {
  catchError,
  filter,
  map,
  shareReplay,
  take,
  takeUntil,
  tap,
  throwError,
  timer,
} from 'rxjs';
import { TagResponse, TagService } from '../../services/tag.service';
import { BaseDialog } from '../dialog/base-dialog';
import { ConfirmHandler } from '../dialog/dialog.model';
import { TagSelectionDialogData } from './services/tag-selection-dialog.service';

@Component({
  selector: 'app-tag-selection-dialog',
  imports: [
    MatButton,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatListOption,
    MatSelectionList,
    MatTooltip,
    MatProgressSpinner,
    MatFormField,
    MatInput,
    MatIcon,
    MatIconButton,
    MatPrefix,
    MatSuffix,
    FormsModule,
  ],
  templateUrl: './tag-selection-dialog.component.html',
  styleUrl: './tag-selection-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TagSelectionDialogComponent extends BaseDialog<
  TagSelectionDialogData & {
    onConfirm?: ConfirmHandler;
  }
> {
  private readonly _dialogRef =
    inject<MatDialogRef<TagSelectionDialogComponent>>(MatDialogRef);

  private readonly _tagService = inject(TagService);

  private readonly _state = signal<TagSelectionDialogData>({});

  protected readonly keyword = signal<string | null>(null);
  protected readonly allTags = signal<TagResponse[]>([]);

  protected readonly filteredTags = computed(() => {
    const kw = this.keyword()?.toLowerCase();
    const tags = this.allTags();

    if (!kw) {
      return tags;
    }

    return tags.filter((t) => t.label.toLowerCase().includes(kw));
  });

  protected readonly selection = viewChild.required(MatSelectionList);

  protected readonly submitted = signal(false);
  protected readonly loading = signal(false);

  protected readonly maxSelectionLimit = computed(() => {
    const { maxSelectionLimit, initialSelectionCount = 0 } = this._state();

    return maxSelectionLimit
      ? Math.max(0, maxSelectionLimit - initialSelectionCount)
      : undefined;
  });

  protected readonly disabledTagLabels = computed(() => {
    return new Set(this._state().disabledLabels ?? []);
  });

  protected readonly isClientScoped = computed(() => {
    return !!this._state().clientId;
  });

  get multiple() {
    return this._state().multiple ?? true;
  }

  get selectedItems(): TagResponse[] {
    return this.selection().selectedOptions.selected.map(({ value }) => value);
  }

  constructor() {
    super();

    this._setState(this.data);

    const clientId = this.data.clientId;
    const disabledLabels = new Set(this.data.disabledLabels ?? []);

    if (clientId) {
      // Fetch only tags used by this client's interactions.
      // Already-selected tags that no longer appear in the used set are
      // synthesised as stubs so they stay visible (greyed-out) in the list.
      this._tagService.getUsedTags({ clientId }).subscribe((usedTags) => {
        const usedLabelSet = new Set(usedTags.map((t) => t.label));

        const missingSelected: TagResponse[] = [...disabledLabels]
          .filter((label) => !usedLabelSet.has(label))
          .map((label) => ({ id: '', label }));

        this.allTags.set([...usedTags, ...missingSelected]);
      });
    } else {
      this._tagService.getAll().subscribe((tags) => this.allTags.set(tags));
    }
  }

  isItemDisabled(item: TagResponse) {
    if (this.disabledTagLabels().has(item.label)) {
      return true;
    }

    const isSelected = this.selectedItems.includes(item);
    const limit = this.maxSelectionLimit();

    const limitReached =
      limit != null && this.selection().selectedOptions.selected.length >= limit;

    return limitReached && !isSelected;
  }

  isAlreadyAdded(item: TagResponse): boolean {
    return this.disabledTagLabels().has(item.label);
  }

  handleSearch(value: string) {
    this.keyword.set(value.trim() || null);
  }

  clearSearch() {
    this.keyword.set(null);
  }

  override handleConfirm(): void {
    this.submitted.set(true);

    const selection = this.selectedItems;

    if (this.data.onConfirm) {
      const callback$ = this.data.onConfirm(selection);

      const source$ = callback$.pipe(
        shareReplay(1),
        catchError((error) => {
          this.submitted.set(false);

          return throwError(() => error);
        })
      );

      const loading$ = merge(
        timer(1000).pipe(
          map(() => true),
          takeUntil(source$)
        ),

        source$.pipe(map(() => false))
      );

      loading$
        .pipe(
          tap((state) => this.loading.set(state)),
          filter((state) => !state),
          tap(() => this._dialogRef.close(true)),
          take(1)
        )
        .subscribe();

      return;
    }

    this._dialogRef.close(true);
  }

  private _setState(newState: Partial<TagSelectionDialogData>) {
    this._state.update((currentState) => ({
      ...currentState,
      ...newState,
    }));
  }
}
